// ============================================================
// TextOverlayTool.jsx — manage text layers (Text tab)
// ------------------------------------------------------------
// Add / select / delete layers and edit the selected layer's style, colour,
// alignment, size and (for Editor/Bubble) background opacity. Dragging to
// reposition happens on the canvas itself (EditorCanvas).
// ============================================================

import React from 'react';
import { TEXT_STYLES, TEXT_STYLE_LIST, MAX_TEXT_LAYERS } from '../../utils/textStyles';

const SWATCHES = ['#ffffff', '#000000', '#31DCBA', '#ff5a5f', '#ffd166', '#4d96ff', '#ff7ad9', '#7bd389'];

export default function TextOverlayTool({
  layers,
  activeId,
  onAdd,
  onSelect,
  onUpdate,
  onRemove,
}) {
  const active = layers.find((l) => l.id === activeId) || null;

  // Switching style also seeds the style's default colour, but only if the
  // user hasn't picked a custom colour yet (still the generic white).
  const handleStyle = (styleId) => {
    const def = TEXT_STYLES[styleId]?.defaultColor;
    const changes = { style: styleId };
    if (def && (active.color === '#ffffff' || active.color === '#fff')) changes.color = def;
    onUpdate(active.id, changes);
  };

  return (
    <div className="pe-text">
      <button
        className="pe-btn pe-btn-primary pe-btn-block"
        onClick={onAdd}
        disabled={layers.length >= MAX_TEXT_LAYERS}
      >
        ＋ Add Text {layers.length > 0 && `(${layers.length}/${MAX_TEXT_LAYERS})`}
      </button>

      {layers.length === 0 && (
        <div className="pe-soon" style={{ minHeight: 80 }}>
          Add a text layer, then drag it on the photo to position it.
        </div>
      )}

      {/* Layer list */}
      {layers.length > 0 && (
        <div className="pe-layer-list">
          {layers.map((l) => (
            <div
              key={l.id}
              className={`pe-layer-row${l.id === activeId ? ' active' : ''}`}
              onClick={() => onSelect(l.id)}
            >
              <span className="pe-layer-text">{l.text || '(empty)'}</span>
              <button
                className="pe-layer-del"
                onClick={(e) => { e.stopPropagation(); onRemove(l.id); }}
                title="Delete layer"
              >×</button>
            </div>
          ))}
        </div>
      )}

      {/* Editor for the active layer */}
      {active && (
        <div className="pe-text-editor">
          <textarea
            className="pe-text-input"
            value={active.text}
            onChange={(e) => onUpdate(active.id, { text: e.target.value })}
            rows={2}
            placeholder="Type your text…"
          />

          <div className="pe-field-label">Style</div>
          <div className="pe-style-grid">
            {TEXT_STYLE_LIST.map((s) => (
              <button
                key={s.id}
                className={`pe-style-chip${active.style === s.id ? ' active' : ''}`}
                onClick={() => handleStyle(s.id)}
              >
                {s.label}
              </button>
            ))}
          </div>

          <div className="pe-field-label">Colour</div>
          <div className="pe-swatches">
            {SWATCHES.map((c) => (
              <button
                key={c}
                className={`pe-swatch${active.color === c ? ' active' : ''}`}
                style={{ background: c }}
                onClick={() => onUpdate(active.id, { color: c })}
              />
            ))}
            <input
              type="color"
              className="pe-swatch pe-swatch-custom"
              value={active.color}
              onChange={(e) => onUpdate(active.id, { color: e.target.value })}
              title="Custom colour"
            />
          </div>

          <div className="pe-field-label">Alignment</div>
          <div className="pe-align-row">
            {['left', 'center', 'right'].map((a) => (
              <button
                key={a}
                className={`pe-align-btn${active.align === a ? ' active' : ''}`}
                onClick={() => onUpdate(active.id, { align: a })}
              >
                {a === 'left' ? '⤙' : a === 'right' ? '⤚' : '≡'}
              </button>
            ))}
          </div>

          <div className="pe-field-label">Size</div>
          <input
            type="range"
            className="pe-range"
            min={3}
            max={28}
            value={Math.round(active.sizeFrac * 100)}
            onChange={(e) => onUpdate(active.id, { sizeFrac: Number(e.target.value) / 100 })}
          />

          {(active.style === 'editor' || active.style === 'bubble') && (
            <>
              <div className="pe-field-label">Background opacity</div>
              <input
                type="range"
                className="pe-range"
                min={0}
                max={100}
                value={active.opacity}
                onChange={(e) => onUpdate(active.id, { opacity: Number(e.target.value) })}
              />
            </>
          )}

          <button className="pe-btn pe-btn-ghost pe-btn-block" onClick={() => onRemove(active.id)}>
            Delete this layer
          </button>
        </div>
      )}
    </div>
  );
}
